import React, { useState } from 'react';
import Dropdown from 'react-bootstrap/Dropdown';
import SendPatientEmailModal from './SendPatientEmailModal';
import SendPatientSmsModal from './SendPatientSmsModal';
import SendPatientThankYouModal from './SendPatientThankYouModal';
import SendPatientGlassPickupModal from './SendPatientGlassPickupModal';
import SendPatientLensPickupModal from './SendPatientLensPickupModal';
import SendPatientWelcomeModal from './SendPatientWelcomeModal';
import SendPatientArticlesModal from './SendPatientArticlesModal';
import { getThankYouActionState } from './thankYouChannel';
import { getGlassPickupActionState } from './glassPickupChannel';
import { getLensPickupActionState } from './lensPickupChannel';
import { getWelcomeActionState } from './welcomeChannel';
import PatientDetailIcon from './subparts/PatientDetailIcon';
import { PATIENT_QUICK_ACTIONS } from './subparts/patientDetailTheme';

const CONTACT_LABELS = {
    email: 'email',
    cell: 'cell number',
};

const getContactValue = (patient, contactKey) => {
    if (contactKey === 'email') {
        return patient.email?.trim();
    }

    if (contactKey === 'cell') {
        return patient.cellNumber?.trim();
    }

    return '';
};

const getQuickActionState = (action, patient, commPrefs) => {
    const reasons = [];

    if (action.contactKey && !getContactValue(patient, action.contactKey)) {
        reasons.push(`no ${CONTACT_LABELS[action.contactKey]} on file`);
    }

    if (action.prefKey && commPrefs != null && !commPrefs[action.prefKey]) {
        reasons.push('disabled in communication preferences');
    }

    if (action.disableIfWelcomeSent && patient.isWelcome) {
        reasons.push('welcome already sent');
    }

    return {
        disabled: reasons.length > 0,
        title: reasons.length > 0 ? `${action.label} — ${reasons.join('; ')}` : action.label,
    };
};

const resolveActionState = (action, patient, commPrefs) => {
    if (action.label === 'Thank You') {
        return getThankYouActionState(patient, commPrefs);
    }

    if (action.label === 'Glass Pickup') {
        return getGlassPickupActionState(patient, commPrefs);
    }

    if (action.label === 'Lens Pickup') {
        return getLensPickupActionState(patient, commPrefs);
    }

    if (action.label === 'Welcome') {
        return getWelcomeActionState(patient, commPrefs);
    }

    return getQuickActionState(action, patient, commPrefs);
};

function PatientQuickActionsMenu({ patient, commPrefs, clinicId, onPatientUpdated }) {
    const [showSendEmailModal, setShowSendEmailModal] = useState(false);
    const [showSendSmsModal, setShowSendSmsModal] = useState(false);
    const [showThankYouModal, setShowThankYouModal] = useState(false);
    const [thankYouChannel, setThankYouChannel] = useState('email');
    const [showGlassPickupModal, setShowGlassPickupModal] = useState(false);
    const [glassPickupChannel, setGlassPickupChannel] = useState('email');
    const [showLensPickupModal, setShowLensPickupModal] = useState(false);
    const [lensPickupChannel, setLensPickupChannel] = useState('email');
    const [showWelcomeModal, setShowWelcomeModal] = useState(false);
    const [welcomeChannel, setWelcomeChannel] = useState('email');
    const [showArticlesModal, setShowArticlesModal] = useState(false);

    const handleActionClick = (action, state) => {
        if (state.disabled) {
            return;
        }

        switch (action.label) {
            case 'Send Email':
                setShowSendEmailModal(true);
                break;
            case 'Send SMS':
                setShowSendSmsModal(true);
                break;
            case 'Thank You':
                setThankYouChannel(state.channel);
                setShowThankYouModal(true);
                break;
            case 'Glass Pickup':
                setGlassPickupChannel(state.channel);
                setShowGlassPickupModal(true);
                break;
            case 'Lens Pickup':
                setLensPickupChannel(state.channel);
                setShowLensPickupModal(true);
                break;
            case 'Welcome':
                setWelcomeChannel(state.channel);
                setShowWelcomeModal(true);
                break;
            case 'Send Articles':
                setShowArticlesModal(true);
                break;
            default:
                break;
        }
    };

    return (
        <>
            <Dropdown align="end" className="patient-detail-actions-dropdown">
                <Dropdown.Toggle
                    variant="light"
                    size="sm"
                    className="patient-detail-actions-dropdown__toggle"
                    id="patient-detail-actions-menu"
                >
                    <i className="bi bi-send" aria-hidden="true" />
                    <span>Actions</span>
                </Dropdown.Toggle>

                <Dropdown.Menu className="patient-detail-actions-dropdown__menu">
                    {PATIENT_QUICK_ACTIONS.map((action) => {
                        const state = resolveActionState(action, patient, commPrefs);

                        return (
                            <Dropdown.Item
                                key={action.label}
                                as="button"
                                type="button"
                                disabled={state.disabled}
                                title={state.title}
                                className="patient-detail-actions-dropdown__item"
                                onClick={() => handleActionClick(action, state)}
                            >
                                <PatientDetailIcon icon={action.icon} theme={action.theme} size="sm" />
                                <span>{action.label}</span>
                            </Dropdown.Item>
                        );
                    })}
                </Dropdown.Menu>
            </Dropdown>

            <SendPatientEmailModal
                show={showSendEmailModal}
                onClose={() => setShowSendEmailModal(false)}
                clinicId={clinicId}
                patient={patient}
            />

            <SendPatientSmsModal
                show={showSendSmsModal}
                onClose={() => setShowSendSmsModal(false)}
                clinicId={clinicId}
                patient={patient}
            />

            <SendPatientThankYouModal
                show={showThankYouModal}
                onClose={() => setShowThankYouModal(false)}
                clinicId={clinicId}
                patient={patient}
                channel={thankYouChannel}
            />

            <SendPatientGlassPickupModal
                show={showGlassPickupModal}
                onClose={() => setShowGlassPickupModal(false)}
                clinicId={clinicId}
                patient={patient}
                channel={glassPickupChannel}
            />

            <SendPatientLensPickupModal
                show={showLensPickupModal}
                onClose={() => setShowLensPickupModal(false)}
                clinicId={clinicId}
                patient={patient}
                channel={lensPickupChannel}
            />

            <SendPatientWelcomeModal
                show={showWelcomeModal}
                onClose={() => setShowWelcomeModal(false)}
                onSent={onPatientUpdated}
                clinicId={clinicId}
                patient={patient}
                channel={welcomeChannel}
            />

            <SendPatientArticlesModal
                show={showArticlesModal}
                onClose={() => setShowArticlesModal(false)}
                clinicId={clinicId}
                patient={patient}
            />
        </>
    );
}

export default PatientQuickActionsMenu;
